/**
 * Logic thuần của tờ "Thêm khách" / "Sửa khách" (STT 2.1, ADR-089): kiểm sớm, thân gửi máy chủ, quyền hiển thị.
 *
 * Kiểm bằng ĐÚNG hàm của máy chủ (`@sapa/shared`) — bài học 30/09: không viết lại hàm kiểm dữ liệu dùng chung.
 * Máy chủ vẫn kiểm lại tất cả; ở đây chỉ để báo sớm, đúng câu.
 */
import {
  chuanHoaLuuY,
  chuanHoaSdtVn,
  chuanHoaSdtVnChiTiet,
  hienSdtVn,
  LUU_Y_TOI_DA,
  lamSachTenKhach,
} from '@sapa/shared'
import { KH } from './text/khach.ts'

export const LUU_Y_CHU_TOI_DA = LUU_Y_TOI_DA

export interface FormKhach {
  ten: string
  sdt: string
  luuY: string
}

export interface LoiFormKhach {
  ten: string | null
  sdt: string | null
  luuY: string | null
}

/** Giá trị đang lưu của hồ sơ — để biết ô nào đã đổi. */
export interface KhachGoc {
  ten: string
  /** `+84…` hoặc `null` (chưa có số). */
  sdt: string | null
  luuY: string | null
}

/**
 * `goc` có mặt = đang SỬA: tên không đổi thì không bị bắt lỗi (hồ sơ "Khách Zalo" cũ vẫn sửa được lưu ý).
 */
export function kiemFormKhach(f: FormKhach, goc?: KhachGoc, chiThemLuuY = false): LoiFormKhach {
  const tenDoi = goc === undefined || f.ten !== goc.ten
  const ten = tenDoi && lamSachTenKhach(f.ten) === null ? KH.thieuTen : null
  const sdtTrong = f.sdt.trim() === ''
  const sdt = !sdtTrong && chuanHoaSdtVnChiTiet(f.sdt).so === null ? KH.soSai : null
  const luuYCuoi = luuYSeGui(goc?.luuY ?? null, f.luuY, chiThemLuuY)
  const luuY = chuanHoaLuuY(luuYCuoi).daCat ? KH.luuYDai : null
  return { ten, sdt, luuY }
}

/**
 * Lưu ý SẼ lưu. Lễ tân chỉ được thêm (`chiThem`): ô là phần THÊM VÀO, ghép sau lưu ý có sẵn bằng một dòng mới;
 * vai khác: ô là toàn bộ lưu ý. Rỗng → `null`.
 */
export function luuYSeGui(cu: string | null, o: string, chiThem: boolean): string | null {
  const moi = o.trim()
  if (!chiThem) return moi === '' ? null : moi
  const goc = cu === null || cu.trim() === '' ? null : cu.trim()
  if (moi === '') return goc
  return goc === null ? moi : `${goc}\n${moi}`
}

export function coTheLuuKhach(f: FormKhach, goc?: KhachGoc, chiThemLuuY = false): boolean {
  const l = kiemFormKhach(f, goc, chiThemLuuY)
  return l.ten === null && l.sdt === null && l.luuY === null
}

/** Số chữ còn lại của lưu ý (đếm theo ký tự, không cắt đôi emoji). */
export function soChuConLai(luuY: string): number {
  return Math.max(0, LUU_Y_CHU_TOI_DA - [...luuY].length)
}

export interface ThanThemKhach {
  ten: string
  sdt: string | null
  luuY: string | null
}

export function thanThemKhach(f: FormKhach): ThanThemKhach {
  const sdt = f.sdt.trim()
  const luuY = f.luuY.trim()
  return { ten: f.ten.trim(), sdt: sdt === '' ? null : sdt, luuY: luuY === '' ? null : luuY }
}

export interface ThanSuaKhach {
  ten?: string
  sdt?: string | null
  luuY?: string | null
}

/**
 * Chỉ trường ĐÃ ĐỔI (thiếu = giữ nguyên ở máy chủ). Không đổi gì → `null` (nút Lưu khoá).
 * Vai không được đổi số (`coDoiSo = false`): `sdt` không bao giờ có trong thân.
 */
export function thanSuaKhach(
  goc: KhachGoc,
  f: FormKhach,
  coDoiSo: boolean,
  chiThemLuuY = false,
): ThanSuaKhach | null {
  const than: ThanSuaKhach = {}
  const ten = f.ten.trim()
  if (ten !== '' && ten !== goc.ten.trim()) than.ten = ten
  if (coDoiSo) {
    const sdt = truongSdtDoi(goc.sdt, f.sdt)
    if (sdt !== undefined) than.sdt = sdt
  }
  const luuYMoi = luuYSeGui(goc.luuY, f.luuY, chiThemLuuY)
  const luuYCu = goc.luuY === null || goc.luuY.trim() === '' ? null : goc.luuY.trim()
  if (luuYMoi !== luuYCu) than.luuY = luuYMoi
  return Object.keys(than).length === 0 ? null : than
}

/**
 * Bấm "Mở hồ sơ" (trùng số) sẽ BỎ một lưu ý vừa gõ không? Thêm khách: ô lưu ý có chữ. Sửa khách: lưu ý sẽ lưu
 * khác lưu ý đang có và không rỗng (xoá hết lưu ý không phải "lưu ý vừa gõ"). Có → hỏi trước, không bỏ im lặng.
 */
export function seMatLuuYKhiMoHoSo(f: FormKhach, goc?: KhachGoc, chiThemLuuY = false): boolean {
  const moi = luuYSeGui(goc?.luuY ?? null, f.luuY, chiThemLuuY)
  if (moi === null) return false
  if (goc === undefined) return true
  const cu = goc.luuY === null || goc.luuY.trim() === '' ? null : goc.luuY.trim()
  return moi !== cu
}

/** `undefined` = số không đổi; `null` = gỡ số; chuỗi = số mới (ô thô — máy chủ chuẩn hoá). */
function truongSdtDoi(sdtGoc: string | null, o: string): string | null | undefined {
  const sdt = o.trim()
  if (sdt === '') return sdtGoc === null ? undefined : null
  const moi = chuanHoaSdtVn(sdt)
  // Số chưa đọc được vẫn gửi (máy chủ trả câu đúng); số trùng số cũ sau chuẩn hoá thì không đổi.
  return moi !== null && moi === sdtGoc ? undefined : sdt
}

/** Câu báo sau khi sửa xong: nhắc thêm khi số cũ 11 chữ số vừa được đổi đầu số. */
export function cauDaLuuSua(r: { sdtDaDoi: boolean }, than: ThanSuaKhach): string {
  const so = than.sdt === undefined || than.sdt === null ? null : chuanHoaSdtVn(than.sdt)
  return r.sdtDaDoi && so !== null
    ? `${KH.daLuu} ${KH.soCuDaDoi(giuSoLienMach(hienSdtVn(so)))}`
    : KH.daLuu
}

export interface QuyenKhach {
  /** Nút "Thêm" ở màn Khách + sửa tên / lưu ý ở hồ sơ. */
  coThem: boolean
  /** "Nhập danh sách khách" — chỉ chủ tiệm. */
  coNhap: boolean
  /** Sửa số điện thoại — chỉ chủ tiệm / quản lý. */
  coDoiSo: boolean
}

export function quyenKhach(toi: { vai: string; quyen: readonly string[] }): QuyenKhach {
  const coThem = toi.quyen.includes('dat-lich')
  return {
    coThem,
    coNhap: coThem && toi.vai === 'owner' && toi.quyen.includes('tim-khach'),
    coDoiSo: coThem && (toi.vai === 'owner' || toi.vai === 'manager'),
  }
}

/**
 * Ô tìm không ra → "Thêm khách" điền sẵn: gõ là số ĐỦ HỢP LỆ → ô Số; gõ là chữ → ô Tên. Đoạn số dở dang (3–4 số
 * cuối — đúng như gợi ý ở ô tìm) KHÔNG được đẩy vào ô Số: tờ sẽ đỏ "Số điện thoại chưa đúng" ngay khi mở
 * (ui-critic vòng 35) — để trống, người dùng tự gõ.
 */
export function dienSanTuO(goVao: string): Partial<FormKhach> {
  const g = goVao.trim()
  if (g === '') return {}
  if (/^[\d\s+.-]+$/.test(g)) return chuanHoaSdtVn(g) === null ? {} : { sdt: g }
  return { ten: g }
}

/**
 * Số điện thoại đứng nguyên một cụm (không bị bẻ giữa dòng ở màn hẹp): dấu cách thường GIỮA HAI CHỮ SỐ → dấu
 * cách không ngắt. Chỉ để HIỂN THỊ — dữ liệu lưu giữ nguyên.
 */
export function giuSoLienMach(chu: string): string {
  return chu.replace(/(\d) (?=\d)/g, '$1\u00a0')
}
